import React, { memo } from 'react';
import {
  Search, Filter, Save, Bookmark, X
} from 'lucide-react';
import MultiSelectDropdown from '../MultiSelectDropdown';

// Lead Status options (for filter dropdown)
const LEAD_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'dnp', label: 'DNP' },
  { value: 'callback_later', label: 'Callback Later' },
  { value: 'future_prospect', label: 'Future Prospect' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'number_not_in_service', label: 'Number Not in Service' },
  { value: 'wip', label: 'WIP' },
  { value: 'not_eligible', label: 'Not Eligible' },
];

const ENROLLMENT_STATUS_OPTIONS = [
  { value: 'not_enrolled', label: 'Not Enrolled' },
  { value: 'payment_pending', label: 'Payment Pending' },
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'expired', label: 'Expired' },
];

const TRIAL_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'no_show', label: 'No Show' },
  { value: 'cancelled', label: 'Cancelled' },
];

const CONSULTATION_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'no_show', label: 'No Show' },
  { value: 'not_interested', label: 'Not Interested' },
];

const COUNTRY_OPTIONS = [
  { value: 'India', label: 'India' },
  { value: 'United States', label: 'United States' },
  { value: 'United Kingdom', label: 'United Kingdom' },
  { value: 'Canada', label: 'Canada' },
  { value: 'Australia', label: 'Australia' },
  { value: 'United Arab Emirates', label: 'UAE' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'Malaysia', label: 'Malaysia' },
  { value: 'Germany', label: 'Germany' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia' },
  { value: 'Qatar', label: 'Qatar' },
  { value: 'New Zealand', label: 'New Zealand' },
  { value: 'International', label: 'International' },
  { value: 'Other', label: 'Other' },
];

const FUNNEL_TYPE_OPTIONS = [
  { value: 'Website', label: 'Website' },
  { value: 'Social Media', label: 'Social Media' },
  { value: 'Referral', label: 'Referral' },
  { value: 'Doctor Referral', label: 'Doctor Referral' },
  { value: 'Performance Marketing', label: 'Performance Marketing' },
  { value: 'Consultation', label: 'Consultation' },
  { value: 'Other', label: 'Other' },
];

const ModeToggle = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="text-xs border-none bg-transparent text-indigo-600 font-medium p-0 cursor-pointer"
  >
    <option value="equals">Equals</option>
    <option value="not_equals">Not Equals</option>
  </select>
);

const LeadFilters = memo(({
  searchTerm, setSearchTerm,
  showFilters, setShowFilters,
  filters, setFilters,
  leadStatusFilter, setLeadStatusFilter,
  batches,
  savedViews, activeView, applyView, resetFilters,
  onSaveView, onDeleteView,
  activeFilterCount,
}) => {
  // Build trial slot options from batches
  const trialSlotOptions = [];
  (batches || []).filter(b => b.active_status).forEach(batch => {
    (batch.slots || []).filter(s => s.active_status).forEach(slot => {
      trialSlotOptions.push({
        value: slot.slot_id,
        label: `${batch.batch_name} - ${slot.slot_name} (${slot.slot_time_start})`,
      });
    });
  });

  return (
    <div className="space-y-4">
      {/* Saved Views Tabs */}
      {savedViews.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <button
            onClick={resetFilters}
            className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
              !activeView ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid="all-leads-view-btn"
          >
            All Leads
          </button>
          {savedViews.map(view => (
            <div key={view.view_id} className="flex items-center gap-1">
              <button
                onClick={() => applyView(view)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  activeView === view.view_id ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Bookmark className="w-3 h-3 inline mr-1" />
                {view.name}
              </button>
              <button onClick={() => onDeleteView(view.view_id)} className="p-1 text-slate-400 hover:text-red-500 rounded">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search & Filter Toggle */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-base pl-10"
            data-testid="search-leads"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn-secondary flex items-center gap-2 ${activeFilterCount > 0 ? 'border-indigo-500 text-indigo-600' : ''}`}
          data-testid="toggle-filters-btn"
        >
          <Filter className="w-4 h-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-indigo-600 text-white text-xs px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <>
            <button onClick={onSaveView} className="btn-secondary flex items-center gap-2" disabled={savedViews.length >= 5}>
              <Save className="w-4 h-4" />
              Save View
            </button>
            <button onClick={resetFilters} className="btn-ghost text-sm text-slate-500">Clear Filters</button>
          </>
        )}
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 animate-fade-in" data-testid="filters-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Lead Status */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-slate-500">Lead Status</label>
                <ModeToggle
                  value={filters.lead_status_mode}
                  onChange={(v) => setFilters({ ...filters, lead_status_mode: v })}
                />
              </div>
              <MultiSelectDropdown
                options={LEAD_STATUS_OPTIONS}
                selected={leadStatusFilter}
                onChange={setLeadStatusFilter}
                placeholder="All Statuses"
                testId="lead-status-filter"
              />
            </div>

            {/* Enrollment Status */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-slate-500">Enrollment Status</label>
                <ModeToggle
                  value={filters.enrollment_status_mode}
                  onChange={(v) => setFilters({ ...filters, enrollment_status_mode: v })}
                />
              </div>
              <MultiSelectDropdown
                options={ENROLLMENT_STATUS_OPTIONS}
                selected={filters.enrollment_status}
                onChange={(vals) => setFilters({ ...filters, enrollment_status: vals })}
                placeholder="All"
                testId="enrollment-status-filter"
              />
            </div>

            {/* Trial Status */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-slate-500">Trial Status</label>
                <ModeToggle
                  value={filters.trial_status_mode}
                  onChange={(v) => setFilters({ ...filters, trial_status_mode: v })}
                />
              </div>
              <MultiSelectDropdown
                options={TRIAL_STATUS_OPTIONS}
                selected={filters.trial_status}
                onChange={(vals) => setFilters({ ...filters, trial_status: vals })}
                placeholder="All"
                testId="trial-status-filter"
              />
            </div>

            {/* Consultation Status */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-slate-500">Consultation Status</label>
                <ModeToggle
                  value={filters.consultation_status_mode}
                  onChange={(v) => setFilters({ ...filters, consultation_status_mode: v })}
                />
              </div>
              <MultiSelectDropdown
                options={CONSULTATION_STATUS_OPTIONS}
                selected={filters.consultation_status}
                onChange={(vals) => setFilters({ ...filters, consultation_status: vals })}
                placeholder="All"
                testId="consultation-status-filter"
              />
            </div>

            {/* Country */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-slate-500">Country</label>
                <ModeToggle
                  value={filters.country_mode}
                  onChange={(v) => setFilters({ ...filters, country_mode: v })}
                />
              </div>
              <MultiSelectDropdown
                options={COUNTRY_OPTIONS}
                selected={filters.country}
                onChange={(vals) => setFilters({ ...filters, country: vals })}
                placeholder="All Countries"
                testId="country-filter"
              />
            </div>

            {/* HOT leads (LMS lead score > 20, or flagged HOT by the LMS) */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Lead Temperature</label>
              <select
                value={filters.is_hot}
                onChange={(e) => setFilters({ ...filters, is_hot: e.target.value })}
                className="input-base"
                data-testid="hot-lead-filter"
              >
                <option value="">All Leads</option>
                <option value="true">HOT only (score &gt; 20)</option>
                <option value="false">Not HOT</option>
              </select>
            </div>

            {/* Funnel Type */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Funnel Type</label>
              <MultiSelectDropdown
                options={FUNNEL_TYPE_OPTIONS}
                selected={filters.funnel_type}
                onChange={(vals) => setFilters({ ...filters, funnel_type: vals })}
                placeholder="All Types"
                testId="funnel-type-filter"
              />
            </div>

            {/* Trial Slot */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Trial Slot</label>
              <MultiSelectDropdown
                options={trialSlotOptions}
                selected={filters.trial_slot}
                onChange={(vals) => setFilters({ ...filters, trial_slot: vals })}
                placeholder="All Slots"
                testId="trial-slot-filter"
              />
            </div>

            {/* Trial Date Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Trial Date</label>
              <select
                value={filters.trial_date_filter}
                onChange={(e) => setFilters({ ...filters, trial_date_filter: e.target.value, trial_date_from: '', trial_date_to: '' })}
                className="input-base"
              >
                <option value="all">All</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="tomorrow">Tomorrow</option>
                <option value="custom">Custom Date</option>
              </select>
            </div>
            {filters.trial_date_filter === 'custom' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Trial From</label>
                  <input type="date" value={filters.trial_date_from} onChange={(e) => setFilters({ ...filters, trial_date_from: e.target.value })} className="input-base" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Trial To</label>
                  <input type="date" value={filters.trial_date_to} onChange={(e) => setFilters({ ...filters, trial_date_to: e.target.value })} className="input-base" />
                </div>
              </>
            )}

            {/* Create Date Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Create Date</label>
              <select
                value={filters.create_date_filter}
                onChange={(e) => setFilters({ ...filters, create_date_filter: e.target.value, create_date_from: '', create_date_to: '' })}
                className="input-base"
              >
                <option value="all">All</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7days">Last 7 Days</option>
                <option value="custom">Custom Date</option>
              </select>
            </div>
            {filters.create_date_filter === 'custom' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Created From</label>
                  <input type="date" value={filters.create_date_from} onChange={(e) => setFilters({ ...filters, create_date_from: e.target.value })} className="input-base" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Created To</label>
                  <input type="date" value={filters.create_date_to} onChange={(e) => setFilters({ ...filters, create_date_to: e.target.value })} className="input-base" />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

LeadFilters.displayName = 'LeadFilters';
export default LeadFilters;
